import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { ROLES } from '../datasheet';
import UserAccessModal from './UserAccessModal';
import { fmtDateTime, fmtDateTimeZone, fmtUntil } from '../timefmt';

const USER_REGIONS = { AU: 'AU only', US: 'US only', GLOBAL: 'Global (AU + US)' };
const LINK_STATE = {
  connected: ['Linked', '#16a34a'],
  expiring_soon: ['Expires soon', '#b45309'],
  reconnect_required: ['Needs relink', '#dc2626'],
  not_linked: ['Not linked', '#6b7280'],
};

export default function AdminView({ toast, isMaster }) {
  const [tab, setTab] = useState('users');
  const tabs = [['users', 'Users & Roles'], ['allowed', 'Allowed Users'], ['salesteam', 'Sales Team Groups'], ['netsuite', 'NetSuite'], ['audit', 'Audit Log'], ...(isMaster ? [['danger', 'Danger Zone']] : [])];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', height: '100%' }}>
      <aside style={{ background: 'var(--surface)', borderRight: '1px solid var(--border)', padding: 14 }}>
        <h3 style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-soft)', margin: '0 0 12px 4px' }}>Admin</h3>
        {tabs.map(([k, l]) => (
          <button key={k} data-testid={'admin-tab-' + k} className="btn" style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 4, background: tab === k ? '#eaf0f7' : 'transparent', borderColor: tab === k ? '#c5d2e0' : 'transparent', color: k === 'danger' ? '#dc2626' : undefined }} onClick={() => setTab(k)}>{l}</button>
        ))}
      </aside>
      <section style={{ overflowY: 'auto', padding: '22px 26px' }}>
        {tab === 'users' && <Users toast={toast} />}
        {tab === 'allowed' && <Allowed toast={toast} />}
        {tab === 'salesteam' && <SalesTeam toast={toast} />}
        {tab === 'netsuite' && <NetSuite toast={toast} isMaster={isMaster} />}
        {tab === 'audit' && <Audit toast={toast} />}
        {tab === 'danger' && isMaster && <DangerZone toast={toast} />}
      </section>
    </div>
  );
}

function DangerZone({ toast }) {
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  async function wipe() {
    setBusy(true);
    try {
      const r = await api.adminWipeData();
      setResult(r.wiped);
      setConfirmText('');
      toast('All data wiped — fresh start');
    } catch (e) { toast(e?.response?.data?.detail || 'Wipe failed', true); }
    setBusy(false);
  }
  return (
    <>
      <h2 style={{ color: '#dc2626' }}>Danger Zone</h2>
      <div className="panel" style={{ maxWidth: 620, border: '1px solid #dc2626', borderRadius: 8, padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>Wipe all data (fresh start)</h3>
        <p className="field-hint">Permanently deletes every datasheet, Core Range item, family, project, proposal, quote, archive file, image and synced sales data <b>in this environment</b>. User accounts, the allowlist, the audit log and Sales Team groups are kept. This cannot be undone.</p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
          <input className="input" data-testid="wipe-confirm-input" style={{ maxWidth: 200 }} placeholder='Type WIPE to confirm' value={confirmText} onChange={e => setConfirmText(e.target.value)} />
          <button className="btn btn-danger-solid" data-testid="wipe-btn" disabled={busy || confirmText !== 'WIPE'} onClick={wipe}>{busy ? 'Wiping…' : 'Wipe all data'}</button>
        </div>
        {result && (
          <div data-testid="wipe-result" style={{ marginTop: 12, fontSize: 12, background: '#fef2f2', padding: 10, borderRadius: 6 }}>
            Wiped: {Object.entries(result).filter(([, v]) => v > 0).map(([k, v]) => k + ': ' + v).join(' · ') || 'nothing (already empty)'}
          </div>
        )}
      </div>
    </>
  );
}

function Users({ toast }) {
  const [users, setUsers] = useState([]);
  const [reps, setReps] = useState([]);
  const [editName, setEditName] = useState(null); // {id, value}
  const [accessUser, setAccessUser] = useState(null);
  const load = useCallback(async () => setUsers((await api.listUsers()).items), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.salesIntelRoles().then(r => setReps((r.items || []).map(p => p.name).filter(Boolean))).catch(() => {}); }, []);
  async function setRole(id, role) { if (!role) return; await api.setUserRole(id, role); toast('Role updated'); load(); }
  async function setRegion(id, region) { if (!region) return; await api.setUserRegion(id, region); toast('Region updated'); load(); }
  async function setNsRep(id, rep) { await api.setUserNsRep(id, rep); toast(rep ? 'NetSuite rep linked' : 'Back to automatic name matching'); load(); }
  async function saveName(u, value) {
    const name = value.trim();
    if (!name || name === (u.name || '')) { setEditName(null); return; }
    try { await api.setUserName(u.id, name); toast('Name updated to "' + name + '"'); setEditName(null); load(); }
    catch (e) { toast(e?.response?.data?.detail || 'Could not update the name', true); }
  }
  return (
    <>
      <h2 style={{ color: 'var(--brand-navy)' }}>Users &amp; Roles</h2>
      <p className="field-hint">Region controls which site data a user can see. <strong>Global</strong> users can switch between AU &amp; US; AU/US users are locked to their region. <strong>Name</strong> is what appears on quotes and datasheets — correct a spelling here (it also fixes the NetSuite “created by” match) and Microsoft sign-in will not overwrite it. <strong>NetSuite rep</strong> drives the personal Sales Intelligence report on the Dashboard — it auto-matches by name, or pick one here to override.</p>
      <table className="adm-table" style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderCollapse: 'collapse' }}>
        <thead><tr>{['Name', 'Email', 'Role', 'Region', 'NetSuite Rep', 'Change Role', 'Change Region', 'Page access'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>{users.map(u => (
          <tr key={u.id}>
            <td style={td}>
              {editName && editName.id === u.id ? (
                <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                  <input className="input" data-testid={'user-name-input-' + u.id} autoFocus value={editName.value} maxLength={80}
                    onChange={e => setEditName({ id: u.id, value: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter') saveName(u, editName.value); if (e.key === 'Escape') setEditName(null); }}
                    style={{ maxWidth: 170 }} />
                  <button className="btn btn-sm" data-testid={'user-name-save-' + u.id} onClick={() => saveName(u, editName.value)}>Save</button>
                  <button className="btn btn-sm" data-testid={'user-name-cancel-' + u.id} onClick={() => setEditName(null)}>✕</button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span data-testid={'user-name-' + u.id}>{u.name || '—'}</span>
                  <button className="btn btn-sm" data-testid={'user-name-edit-' + u.id} title="Edit this user's display name"
                    onClick={() => setEditName({ id: u.id, value: u.name || '' })}>Edit</button>
                </div>
              )}
            </td>
            <td style={td}>{u.email}</td><td style={td}>{ROLES[u.role] || u.role}</td>
            <td style={td} data-testid={'user-region-' + u.id}>{USER_REGIONS[u.region] || u.region || 'AU'}</td>
            <td style={td}><select className="select" data-testid={'user-nsrep-select-' + u.id} value={u.ns_rep || ''} onChange={e => setNsRep(u.id, e.target.value)} style={{ maxWidth: 180 }} title="NetSuite sales rep for the personal dashboard report">
              <option value="">Auto (name match)</option>
              {reps.map(n => <option key={n} value={n}>{n}</option>)}
              {u.ns_rep && !reps.includes(u.ns_rep) && <option value={u.ns_rep}>{u.ns_rep}</option>}
            </select></td>
            <td style={td}><select className="select" data-testid={'user-role-select-' + u.id} defaultValue="" onChange={e => setRole(u.id, e.target.value)} style={{ maxWidth: 170 }}>
              <option value="" disabled>Change…</option>
              {Object.entries(ROLES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></td>
            <td style={td}><select className="select" data-testid={'user-region-select-' + u.id} defaultValue="" onChange={e => setRegion(u.id, e.target.value)} style={{ maxWidth: 190 }}>
              <option value="" disabled>Change…</option>
              {Object.entries(USER_REGIONS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></td>
            <td style={td}>
              {u.role === 'master_admin' ? <span className="field-hint" style={{ margin: 0 }}>Everything</span> : (
                <button className="btn btn-sm" data-testid={'user-access-btn-' + u.id} onClick={() => setAccessUser(u)}
                  title="Give or remove access to individual pages for this person">
                  {Object.keys(u.perm_overrides || {}).length
                    ? Object.keys(u.perm_overrides).length + ' custom'
                    : 'Role default'}
                </button>
              )}
            </td></tr>
        ))}</tbody>
      </table>
      {accessUser && (
        <UserAccessModal user={accessUser} toast={toast} onClose={() => setAccessUser(null)}
          onSaved={() => load()} />
      )}
    </>
  );
}

function Allowed({ toast }) {
  const [list, setList] = useState([]);
  const [email, setEmail] = useState(''); const [role, setRole] = useState('sales_rep'); const [name, setName] = useState(''); const [region, setRegion] = useState('AU');
  const [created, setCreated] = useState(null); // {email, temp_password}
  const load = useCallback(async () => setList((await api.listAllowed()).items), []);
  useEffect(() => { load(); }, [load]);
  async function add() {
    if (!email) return;
    try {
      const res = await api.addAllowed(email, role, name, region);
      setEmail(''); setName('');
      setCreated({ email: res.email, temp_password: res.temp_password });
      toast('Account created');
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast(typeof d === 'string' ? d : 'Failed to add user — check the email address is valid');
    }
  }
  async function remove(e) { if (!window.confirm('Remove ' + e + '?')) return; await api.removeAllowed(e); toast('Removed'); load(); }
  return (
    <>
      <h2 style={{ color: 'var(--brand-navy)' }}>Allowed Users</h2>
      <p className="field-hint">Add someone here to create their account. A temporary password is generated for you to give them — they'll be asked to set their own password the first time they sign in. Region controls which site they can access: AU only / US only users never see the region switch; Global users can switch between both.</p>
      {created && (
        <div data-testid="temp-password-box" style={{ background: '#f0fdf4', border: '1px solid #16a34a', borderRadius: 8, padding: '10px 14px', margin: '10px 0', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13 }}>Account for <b>{created.email}</b> created. Temporary password (shown once — copy it now):</span>
          <code data-testid="temp-password-value" style={{ fontSize: 15, fontWeight: 800, background: '#dcfce7', padding: '3px 10px', borderRadius: 6 }}>{created.temp_password}</code>
          <button className="btn btn-sm" data-testid="temp-password-copy" onClick={() => { navigator.clipboard.writeText(created.temp_password).then(() => toast('Copied')); }}>Copy</button>
          <button className="btn btn-sm" data-testid="temp-password-dismiss" onClick={() => setCreated(null)}>Done</button>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', margin: '12px 0', flexWrap: 'wrap' }}>
        <div className="field" style={{ margin: 0, flex: 2, minWidth: 200 }}><label className="field-label">Email</label><input className="input" data-testid="allowed-email-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@lpalighting.com" /></div>
        <div className="field" style={{ margin: 0, minWidth: 150 }}><label className="field-label">Role</label><select className="select" data-testid="allowed-role-select" value={role} onChange={e => { setRole(e.target.value); if (e.target.value === 'us_datasheet_creator') setRegion('US'); }}>{Object.entries(ROLES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
        <div className="field" style={{ margin: 0, minWidth: 160 }}><label className="field-label">Region access</label><select className="select" data-testid="allowed-region-select" value={region} disabled={role === 'us_datasheet_creator'} title={role === 'us_datasheet_creator' ? 'US Datasheet Creators are locked to the US site' : undefined} onChange={e => setRegion(e.target.value)}>{Object.entries(USER_REGIONS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
        <div className="field" style={{ margin: 0, flex: 1, minWidth: 140 }}><label className="field-label">Name</label><input className="input" data-testid="allowed-name-input" value={name} onChange={e => setName(e.target.value)} /></div>
        <button className="btn btn-primary" data-testid="allowed-add-btn" onClick={add}>+ Create account</button>
      </div>
      <table style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderCollapse: 'collapse' }}>
        <thead><tr>{['Email', 'Name', 'Role', 'Region', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>{list.map(a => (<tr key={a.email}><td style={td}>{a.email}</td><td style={td}>{a.name || '—'}</td><td style={td}>{ROLES[a.role] || a.role}</td><td style={td}>{USER_REGIONS[a.region] || a.region || 'AU'}</td><td style={td}><button className="btn btn-sm" data-testid={'allowed-reset-' + a.email} style={{ marginRight: 6 }} onClick={async () => { try { const r = await api.resetAllowedPassword(a.email); setCreated({ email: a.email, temp_password: r.temp_password }); toast('New temporary password issued'); } catch (e) { toast(e?.response?.data?.detail || 'Reset failed'); } }}>Reset password</button><button className="btn btn-sm btn-danger" onClick={() => remove(a.email)}>Remove</button></td></tr>))}</tbody>
      </table>
    </>
  );
}

function SalesTeam({ toast }) {
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.salesIntelRoles().then(r => { setItems(r.items || []); setGroups(r.groups || []); }).catch(() => {});
  }, []);
  const setGroup = (name, group) => { setItems(is => is.map(i => i.name === name ? { ...i, group } : i)); setDirty(true); };
  async function save() {
    setSaving(true);
    try { await api.saveSalesIntelRoles(items); toast('Sales team groups saved'); setDirty(false); }
    catch (e) { toast(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  }
  return (
    <>
      <h2 style={{ color: 'var(--brand-navy)' }}>Sales Team Groups</h2>
      <p className="field-hint">Everyone who appears as a quote creator or sales rep in the NetSuite data (plus every active NetSuite employee) shows up here. Assign each person a group — including “Old Employee” for people who have left. The Sales Intelligence dashboard uses these groups to break down quote activity.</p>
      <div style={{ margin: '12px 0' }}>
        <button className="btn btn-primary" data-testid="salesteam-save-btn" disabled={!dirty || saving} onClick={save}>{saving ? 'Saving…' : 'Save changes'}</button>
      </div>
      {items.length === 0 ? <div className="empty-note" data-testid="salesteam-empty">No people yet — run a Sales Intelligence sync from the Dashboard first.</div> : (
        <table className="adm-table" style={{ width: '100%', maxWidth: 640, background: 'var(--surface)', border: '1px solid var(--border)', borderCollapse: 'collapse' }}>
          <thead><tr>{['Name', 'Group'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>{items.map(p => (
            <tr key={p.name}>
              <td style={td}>{p.name}</td>
              <td style={td}>
                <select className="select" data-testid={'salesteam-group-' + p.name.replace(/\s+/g, '-')} value={p.group} onChange={e => setGroup(p.name, e.target.value)} style={{ maxWidth: 200 }}>
                  <option value="Unassigned">Unassigned</option>
                  {groups.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </>
  );
}

function NetSuite({ toast, isMaster }) {
  const [status, setStatus] = useState(null);
  const [links, setLinks] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadLinks = useCallback(() => {
    api.nsOauthConnections().then(setLinks).catch(() => setLinks(null));
  }, []);
  useEffect(() => { api.nsStatus().then(setStatus).catch(() => setStatus({ configured: false })); }, []);
  useEffect(() => { if (isMaster) loadLinks(); }, [isMaster, loadLinks]);

  async function resetAll() {
    if (!window.confirm('Clear every NetSuite sign-in?\n\nEveryone (including you) will be asked to relink their NetSuite account the next time they load the app. Nothing else is affected.')) return;
    setBusy(true);
    try {
      const r = await api.nsOauthResetAll();
      toast('Cleared ' + r.removed + ' NetSuite sign-in' + (r.removed === 1 ? '' : 's') + ' — everyone will be asked to relink');
      loadLinks();
    } catch (e) {
      toast(e?.response?.data?.detail || 'Could not clear the NetSuite sign-ins', true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h2 style={{ color: 'var(--brand-navy)' }}>NetSuite</h2>
      <div className="panel" style={{ maxWidth: 520 }}>
        {status === null ? 'Checking…' : status.configured
          ? <span style={{ color: 'var(--brand-green-deep)', fontWeight: 700 }}>● Connected (account {status.account_id})</span>
          : <span style={{ color: 'var(--danger)', fontWeight: 700 }}>● Not connected — set the NS_* env vars on the backend.</span>}
        <p className="field-hint" style={{ marginTop: 10 }}>Push datasheets to NetSuite and import items from the editor / library once connected. Secrets live only on the backend.</p>
      </div>
      {isMaster && (
        <div className="panel" style={{ maxWidth: 820, marginTop: 14 }}>
          <h3 style={{ margin: '0 0 6px', fontSize: 14, color: 'var(--brand-navy)' }}>Per-user NetSuite sign-in</h3>
          <p className="field-hint" data-testid="ns-links-count">
            {links === null ? 'Checking…' : !links.enabled ? (links.reason || 'Per-user sign-in is not switched on for this environment.')
              : links.connected + ' of ' + links.total + ' linked. Everyone except US datasheet creators must link before using the app — chase anyone marked below.'}
          </p>
          {links && links.enabled && (
            <div data-testid="ns-links-table" style={{ marginTop: 12, border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead>
                  <tr><th style={th}>Name</th><th style={th}>Role</th><th style={th}>Status</th><th style={th}>Linked</th><th style={th}>Session ends</th></tr>
                </thead>
                <tbody>
                  {links.users.map(u => {
                    const [label, colour] = LINK_STATE[u.state] || LINK_STATE.not_linked;
                    return (
                      <tr key={u.user_id} data-testid={'ns-link-row-' + u.email}>
                        <td style={td}>{u.name}<div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{u.email}</div></td>
                        <td style={td}>{ROLES[u.role] || u.role}</td>
                        <td style={td}>
                          <span style={{ background: colour, color: '#fff', borderRadius: 999, padding: '2px 9px', fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap' }}>{label}</span>
                        </td>
                        <td style={td}>{u.connected_at ? fmtDateTime(u.connected_at) : '—'}</td>
                        <td style={td}>{u.expires_at ? fmtDateTime(u.expires_at) + ' · ' + fmtUntil(u.expires_at) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <button className="btn" data-testid="ns-reset-all-btn" disabled={busy || links === null || !links.enabled}
            style={{ marginTop: 12, borderColor: '#f0b4b4', color: '#dc2626' }} onClick={resetAll}>
            {busy ? 'Clearing…' : 'Force everyone to relink NetSuite'}
          </button>
        </div>
      )}
    </>
  );
}

const AUDIT_CATS = [
  ['', 'All'],
  ['logins', 'Log Ins'],
  ['accounts', 'Account Creations'],
  ['datasheets', 'Datasheet Creations'],
  ['projects', 'Project Creations'],
  ['proposals', 'Proposal Packages'],
  ['quotes', 'Quote Creations'],
  ['netsuite', 'NetSuite Pushes'],
  ['misc', 'Misc.'],
];
const CAT_OF = {
  'auth.login': 'logins', 'auth.microsoft': 'logins',
  'allowed.create': 'accounts', 'user.set_password': 'accounts',
  'product.create': 'datasheets', 'family.create': 'datasheets', 'ai.extract': 'datasheets',
  'project.create': 'projects',
  'proposal.export': 'proposals',
  'quote.create': 'quotes', 'quote.update': 'quotes',
  'netsuite.create_item': 'netsuite', 'netsuite.push_create': 'netsuite', 'netsuite.push_merge': 'netsuite',
  'quote.netsuite_create': 'netsuite', 'quote.netsuite_update': 'netsuite', 'netsuite.update_pricing': 'netsuite',
  'sales_intel.sync': 'netsuite', 'sales_intel.roles_update': 'accounts',
  'netsuite.oauth_reset_all': 'netsuite', 'netsuite.oauth_disconnect': 'netsuite',
};
const CAT_BADGE = {
  logins: ['Log In', '#0ea5e9'], accounts: ['Account', '#8b5cf6'], datasheets: ['Datasheet', '#052748'],
  projects: ['Project', '#0f766e'], proposals: ['Proposal Pack', '#b45309'], quotes: ['Quote', '#16a34a'],
  netsuite: ['NetSuite', '#dc2626'], misc: ['Misc.', '#6b7280'],
};

function Audit({ toast }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cat, setCat] = useState('');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  useEffect(() => {
    setBusy(true);
    const t = setTimeout(() => {
      api.listAudit({ limit: 300, category: cat || undefined, q: q.trim() || undefined })
        .then(r => { setItems(r.items); setTotal(r.total); })
        .catch(() => {})
        .finally(() => setBusy(false));
    }, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [cat, q]);

  async function exportXlsx() {
    setExporting(true);
    try {
      const blob = await api.auditExport({ category: cat || undefined, q: q.trim() || undefined });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'audit-log' + (cat ? '-' + cat : '') + '.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast('Audit log downloaded — times are shown in both Melbourne and UTC');
    } catch (e) {
      toast('Export failed: ' + (e.response?.data?.detail || e.message), true);
    }
    setExporting(false);
  }
  return (
    <>
      <h2 style={{ color: 'var(--brand-navy)' }}>Audit Log</h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {AUDIT_CATS.map(([k, label]) => (
          <button key={k || 'all'} data-testid={'audit-cat-' + (k || 'all')} onClick={() => setCat(k)}
            style={{
              border: '1px solid ' + (cat === k ? 'var(--brand-navy)' : 'var(--border)'),
              background: cat === k ? 'var(--brand-navy)' : 'var(--surface)',
              color: cat === k ? '#fff' : 'var(--text)',
              borderRadius: 999, padding: '5px 13px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>{label}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
        <input className="input" data-testid="audit-search" placeholder="Search user, action, id…" value={q}
          onChange={e => setQ(e.target.value)} style={{ maxWidth: 320 }} />
        <span className="field-hint" data-testid="audit-total">{busy ? 'Loading…' : total + ' entr' + (total === 1 ? 'y' : 'ies')}{items.length < total ? ' (showing latest ' + items.length + ')' : ''}</span>
        <button className="btn" data-testid="audit-export-btn" disabled={exporting || !total}
          style={{ marginLeft: 'auto' }} onClick={exportXlsx}>
          {exporting ? 'Preparing…' : '↓ Export Excel'}
        </button>
      </div>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
        {items.length === 0 ? <div className="empty-note" style={{ border: 'none' }}>No entries.</div> : items.map((e, i) => {
          const [label, color] = CAT_BADGE[CAT_OF[e.action] || 'misc'];
          return (
            <div key={e.id || i} data-testid={'audit-row-' + i} style={{ display: 'grid', gridTemplateColumns: '176px 108px 170px minmax(240px, 1fr)', gap: 12, alignItems: 'baseline', padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12, minWidth: 720 }}>
              <span data-testid="audit-time" style={{ color: 'var(--text-muted)', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11 }}>{fmtDateTimeZone(e.created_at) || '—'}</span>
              <span style={{ justifySelf: 'start', background: color, color: '#fff', borderRadius: 999, padding: '2px 9px', fontSize: 10, fontWeight: 800, letterSpacing: '.03em', whiteSpace: 'nowrap' }}>{label}</span>
              <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.user_email || ''}>{e.user_name || e.user_email}</span>
              <span><code style={{ background: 'var(--surface-2)', padding: '1px 5px', borderRadius: 4 }}>{e.action}</code> {e.metadata && Object.keys(e.metadata).length ? JSON.stringify(e.metadata) : ''}</span>
            </div>
          );
        })}
      </div>
    </>
  );
}

const th = { background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', padding: '9px 11px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-soft)' };
const td = { padding: '9px 11px', borderBottom: '1px solid var(--border)', fontSize: 12.5 };
